<template>
  <div class="dashboard-section card-box">
    <div class="section-header">
      <div class="section-header-title">
        <div class="section-icon text-primary">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </div>
        <h2>Táncnaptár & Közelgő Események</h2>
      </div>
      <router-link to="/calendars" class="modern-btn modern-btn--secondary btn-sm rounded-pill px-3">
        Teljes naptár megtekintése &rarr;
      </router-link>
    </div>

    <div class="section-body">
      <!-- Embedded Interactive Calendar Grid -->
      <div class="calendar-embed-container mb-4">
        <CalendarItem />
      </div>

      <!-- Upcoming Classes, Competitions & Galas 3-Column Summary Cards -->
      <div class="events-summary-grid">
        <!-- Közelgő táncórák -->
        <div class="event-category-card">
          <div class="category-header">
            <div class="category-icon-badge category-icon-blue">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12,6 12,12 16,14"/>
              </svg>
            </div>
            <h3 class="category-title">Közelgő táncórák</h3>
          </div>
          <div v-if="upcomingLessons && upcomingLessons.length" class="category-item-list">
            <div v-for="lesson in upcomingLessons" :key="lesson.id" class="event-item-card">
              <div class="event-title mb-1.5">{{ lesson.name }}</div>
              <div class="event-pills-row">
                <span class="event-pill-badge pill-blue" v-if="lesson.next_dates">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {{ formatDateTime(lesson.next_dates) }}
                </span>
                <span class="event-pill-badge pill-slate" v-if="lesson.schedule">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 16 14"/></svg>
                  {{ parseSchedule(lesson.schedule) }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="empty-category-text">
            Nincs rögzített közelgő táncóra.
          </div>
        </div>

        <!-- Közelgő versenyek -->
        <div class="event-category-card">
          <div class="category-header">
            <div class="category-icon-badge category-icon-amber">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
                <path d="M4 22h16"/>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/>
              </svg>
            </div>
            <h3 class="category-title">Közelgő versenyek</h3>
          </div>
          <div v-if="upcomingCompetitions && upcomingCompetitions.length" class="category-item-list">
            <div v-for="comp in upcomingCompetitions" :key="comp.id" class="event-item-card">
              <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                <div class="event-title">{{ comp.title }}</div>
                <span class="event-pill-badge pill-amber" v-if="comp.comp_date">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2z"/></svg>
                  {{ formatDateTime(comp.comp_date) }}
                </span>
              </div>
              <small class="event-location-text" v-if="comp.location">📍 {{ comp.location }}</small>
            </div>
          </div>
          <div v-else class="empty-category-text">
            Nincs megadva közelgő verseny.
          </div>
        </div>

        <!-- Gálák & Események -->
        <div class="event-category-card">
          <div class="category-header">
            <div class="category-icon-badge category-icon-purple">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
            </div>
            <h3 class="category-title">Gálák & Események</h3>
          </div>
          <div v-if="(upcomingGalas && upcomingGalas.length) || (calendarEvents && calendarEvents.length)" class="category-item-list">
            <div v-for="gala in upcomingGalas" :key="'gala-'+gala.id" class="event-item-card">
              <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                <div class="event-title">{{ gala.name }}</div>
                <span class="event-pill-badge pill-purple">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                  Gála{{ formatDateTime(gala.event_date || gala.date) ? ': ' + formatDateTime(gala.event_date || gala.date) : '' }}
                </span>
              </div>
              <small class="event-location-text" v-if="gala.location">📍 {{ gala.location }}</small>
            </div>
            <div v-for="evt in calendarEvents" :key="'evt-'+evt.id" class="event-item-card">
              <div class="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-1">
                <div class="event-title">{{ evt.title }}</div>
                <span class="event-pill-badge pill-info" v-if="evt.event_date">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {{ formatDateTime(evt.event_date) }}
                </span>
              </div>
              <small class="event-location-text" v-if="evt.location">📍 {{ evt.location }}</small>
            </div>
          </div>
          <div v-else class="empty-category-text">
            Nincs megadva gála vagy egyéb esemény.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CalendarItem from '../CalendarItem.vue';

export default {
  name: 'ChildCalendarEvents',
  components: {
    CalendarItem
  },
  props: {
    upcomingLessons: {
      type: Array,
      default: () => []
    },
    upcomingCompetitions: {
      type: Array,
      default: () => []
    },
    upcomingGalas: {
      type: Array,
      default: () => []
    },
    calendarEvents: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    formatDateOnly(val) {
      if (!val) return '';
      let str = String(val).trim();
      if (str.includes('T')) {
        str = str.split('T')[0];
      } else if (str.includes(' ')) {
        str = str.split(' ')[0];
      }
      return str;
    },
    formatDateTime(val) {
      if (!val) return '';
      let str = String(val).trim();
      if (str.includes('T')) {
        const parts = str.split('T');
        const datePart = parts[0];
        let timePart = parts[1] ? parts[1].replace(/Z.*$/, '').trim() : '';
        if (timePart) {
          const timeSub = timePart.split(':');
          if (timeSub.length >= 2) {
            timePart = `${timeSub[0]}:${timeSub[1]}`;
          }
          return `${datePart} ${timePart}`;
        }
        return datePart;
      }
      if (str.includes(' ')) {
        const parts = str.split(' ');
        const datePart = parts[0];
        let timePart = parts[1] ? parts[1].trim() : '';
        if (timePart) {
          const timeSub = timePart.split(':');
          if (timeSub.length >= 2) {
            timePart = `${timeSub[0]}:${timeSub[1]}`;
          }
          return `${datePart} ${timePart}`;
        }
        return datePart;
      }
      return str;
    },
    parseSchedule(schedule) {
      try {
        const daysOfWeek = ["", "Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"];
        let parsed = JSON.parse(schedule);
        if (!Array.isArray(parsed)) {
          parsed = [parsed];
        }
        return parsed
          .filter(s => s && s.day && s.time)
          .map(s => {
            const dayStr = daysOfWeek[s.day] || s.day;
            const freqStr = s.frequency === 'biweekly' 
              ? ` (kéthetente${s.start_date ? ', kezdve: ' + s.start_date : ''})` 
              : '';
            return `${dayStr} ${s.time}${freqStr}`;
          })
          .join(", ");
      } catch (e) {
        return "Érvénytelen idő adat";
      }
    }
  }
};
</script>

<style scoped>
.dashboard-section {
  margin-bottom: 1.5rem;
}

.card-box {
  background: var(--card-bg);
  border-radius: 20px;
  border: 1px solid var(--color-border);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
  overflow: hidden;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-background-soft);
}

.section-header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-header-title h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-heading);
}

.section-body {
  padding: 1.75rem;
}

.calendar-embed-container {
  display: flex;
  justify-content: center;
}

.events-summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  margin-top: 1rem;
}

@media (max-width: 992px) {
  .events-summary-grid {
    grid-template-columns: 1fr !important;
  }
}

.event-category-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  transition: all 0.25s ease-in-out;
  display: flex !important;
  flex-direction: column !important;
  padding: 1.25rem;
}

.event-category-card:hover {
  box-shadow: 0 10px 28px -6px rgba(0, 0, 0, 0.08), 0 4px 10px -2px rgba(0, 0, 0, 0.04) !important;
}

.category-header {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  gap: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  margin-bottom: 1rem !important;
  border-bottom: 1px solid var(--color-border, #e2e8f0) !important;
}

.category-title {
  color: var(--color-heading, #1e293b);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 !important;
}

.category-icon-badge {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.category-icon-blue {
  background-color: #eff6ff;
  color: #2563eb;
}

.category-icon-amber {
  background-color: #fffbeb;
  color: #d97706;
}

.category-icon-purple {
  background-color: #f3e8ff;
  color: #7c3aed;
}

.category-item-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.event-item-card {
  background-color: var(--color-background-soft, #f8fafc);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 12px;
  padding: 0.875rem 1rem;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}

.event-item-card:hover {
  transform: translateY(-2px);
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.event-title {
  font-weight: 600;
  color: var(--color-heading, #0f172a);
  font-size: 0.9rem;
}

.event-pills-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.event-pill-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.6rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.pill-blue {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}

.pill-slate {
  background: var(--color-background-soft, #f1f5f9);
  color: var(--color-text-muted, #475569);
  border: 1px solid var(--color-border, #e2e8f0);
}

.pill-amber {
  background: #fffbeb;
  color: #d97706;
  border: 1px solid #fde68a;
}

.pill-purple {
  background: #f3e8ff;
  color: #7c3aed;
  border: 1px solid #e9d5ff;
}

.pill-info {
  background: #e0f2fe;
  color: #0284c7;
  border: 1px solid #bae6fd;
}

.event-location-text {
  color: var(--color-text-muted);
  font-size: 0.8rem;
}

.empty-category-text {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  font-style: italic;
  padding: 1rem 0;
  text-align: center;
}

[data-theme="dark"] .event-category-card {
  background: var(--gray-800) !important;
  border-color: var(--gray-700) !important;
}

[data-theme="dark"] .event-item-card {
  background-color: var(--gray-900) !important;
  border-color: var(--gray-700) !important;
}

[data-theme="dark"] .pill-blue {
  background: rgba(37, 99, 235, 0.2) !important;
  color: #60a5fa !important;
  border-color: rgba(37, 99, 235, 0.35) !important;
}

[data-theme="dark"] .pill-slate {
  background-color: var(--bg-secondary) !important;
  color: var(--color-text-muted) !important;
  border-color: var(--color-border) !important;
}

[data-theme="dark"] .pill-amber {
  background-color: rgba(245, 158, 11, 0.2) !important;
  color: #fbbf24 !important;
  border-color: rgba(245, 158, 11, 0.35) !important;
}

[data-theme="dark"] .pill-purple {
  background-color: rgba(168, 85, 247, 0.2) !important;
  color: #c084fc !important;
  border-color: rgba(168, 85, 247, 0.35) !important;
}

[data-theme="dark"] .pill-info {
  background-color: rgba(2, 132, 199, 0.2) !important;
  color: #38bdf8 !important;
  border-color: rgba(2, 132, 199, 0.35) !important;
}
</style>
